import React from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Landmark, 
  Stethoscope, 
  ShoppingBag, 
  Plane, 
  Factory, 
  Server, 
  Briefcase,
  Shield,
  Lock,
  Database,
  Cloud,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

interface IndustriesOverviewProps {
  setCurrentPage: (page: string) => void;
}

const IndustriesOverview: React.FC<IndustriesOverviewProps> = ({ setCurrentPage }) => {
  const industries = [
    {
      icon: Building2,
      title: "Financial Services",
      description: "Comprehensive security solutions for banks, insurance companies, and financial institutions.",
      features: [
        "Regulatory compliance (GDPR, PCI DSS)",
        "Fraud detection and prevention",
        "Secure transaction processing",
        "Data encryption and protection"
      ],
      challenges: [
        "Complex regulatory requirements",
        "Sophisticated cyber threats",
        "High-value targets",
        "Customer data protection"
      ],
      link: "financial-services"
    },
    {
      icon: Stethoscope,
      title: "Healthcare",
      description: "Specialized security solutions for healthcare providers and medical facilities.",
      features: [
        "HIPAA compliance",
        "Patient data protection",
        "Medical device security",
        "Telemedicine security"
      ],
      challenges: [
        "Patient privacy regulations",
        "IoT device security",
        "Legacy systems",
        "Remote access security"
      ],
      link: "healthcare"
    },
    {
      icon: Landmark,
      title: "Government",
      description: "Advanced security measures for government agencies and public sector organizations.",
      features: [
        "Critical infrastructure protection",
        "Classified data security",
        "Cyber threat intelligence",
        "Emergency response systems"
      ],
      challenges: [
        "Nation-state threats",
        "Critical infrastructure",
        "Public safety",
        "Regulatory compliance"
      ],
      link: "government"
    },
    {
      icon: Factory,
      title: "Manufacturing",
      description: "Industrial security solutions for manufacturing and production facilities.",
      features: [
        "Industrial control system security",
        "Supply chain protection",
        "IP protection",
        "OT/IT security integration"
      ],
      challenges: [
        "Operational technology security",
        "Supply chain risks",
        "Industrial espionage",
        "Production continuity"
      ],
      link: "manufacturing"
    },
    {
      icon: ShoppingBag,
      title: "Retail",
      description: "Comprehensive security for retail businesses and e-commerce platforms.",
      features: [
        "POS system security",
        "E-commerce protection",
        "Customer data security",
        "Payment processing security"
      ],
      challenges: [
        "Payment card security",
        "Customer data protection",
        "E-commerce threats",
        "Supply chain security"
      ],
      link: "retail"
    },
    {
      icon: Server,
      title: "Technology",
      description: "Cutting-edge security solutions for technology companies and digital enterprises.",
      features: [
        "Cloud infrastructure security",
        "DevSecOps implementation",
        "API security",
        "Source code protection"
      ],
      challenges: [
        "Rapid innovation cycles",
        "Complex infrastructures",
        "Intellectual property",
        "Third-party integrations"
      ],
      link: "technology"
    }
  ];

  const capabilities = [
    {
      icon: Shield,
      title: "Industry-Specific Compliance",
      description: "Tailored compliance solutions meeting unique regulatory requirements for each sector."
    },
    {
      icon: Lock,
      title: "Custom Security Controls",
      description: "Specialized security measures designed for industry-specific challenges and threats."
    },
    {
      icon: Database,
      title: "Data Protection",
      description: "Advanced data security solutions aligned with industry standards and requirements."
    },
    {
      icon: Cloud,
      title: "Secure Infrastructure",
      description: "Industry-optimized security infrastructure ensuring business continuity."
    }
  ];

  const handleIndustryClick = (link: string) => {
    setCurrentPage(link);
  };

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Building2 className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Industries We Serve</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Specialized cybersecurity solutions tailored to meet the unique challenges and requirements of your industry
          </motion.p>
        </div>

        {/* Core Capabilities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20"
        >
          {capabilities.map((capability, index) => (
            <div key={index} className="glass-panel rounded-xl p-6 hover-card">
              <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg mb-6">
                <capability.icon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{capability.title}</h3>
              <p className="text-muted">{capability.description}</p>
            </div>
          ))}
        </motion.div>

        {/* Industries Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {industries.map((industry, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <industry.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{industry.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{industry.description}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="font-semibold mb-3">Key Features</h4>
                  <ul className="space-y-2">
                    {industry.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                        <span className="text-muted">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-3">Industry Challenges</h4>
                  <ul className="space-y-2">
                    {industry.challenges.map((challenge, challengeIndex) => (
                      <li key={challengeIndex} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                        <span className="text-muted">{challenge}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <button 
                onClick={() => handleIndustryClick(industry.link)}
                className="w-full btn-primary"
              >
                <span className="flex items-center justify-center">
                  Learn More
                  <ArrowRight className="ml-2 h-5 w-5" />
                </span>
              </button>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Secure Your Industry?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our experts to discuss your industry-specific security needs and discover how we can help protect your organization
            </p>
            <button 
              onClick={() => handleIndustryClick('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default IndustriesOverview;